import styled from 'styled-components';
import { BarIconButton, BarIconTrigger } from 'components/Widget/BarIconButton/styles';

export const CHAT_TAB_BAR_HEIGHT = 36;

export const Base = styled.div`
	// layout
	height: ${CHAT_TAB_BAR_HEIGHT}px;
	padding-right: 4px;
	display: flex;
	align-items: center;
	gap: 4px;
	min-width: 0;
	position: relative;

	// style: same tab-bar language as the DS editor's section bar
	background: transparent;
	justify-content: space-between;

	// The bar's hairline is drawn underneath the tabs rather than as a border, so
	// the active tab's own fill covers the 1px beneath it and the line reads as
	// notched. Keeping it inside the tab's box means the strip can clip freely.
	&::after {
		content: '';
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		height: 1px;
		background: ${({ theme }) => theme.gray[200]};
		z-index: 0;
		pointer-events: none;
	}
`;

// Tabs stop shrinking at their min-width, so without this they would spill past
// the strip and over the controls beside it.
export const TabStrip = styled.div`
	display: flex;
	align-items: center;
	gap: 0;
	height: 100%;
	flex: 1 1 auto;
	min-width: 0;
	overflow: hidden;
`;

// hover-revealed close, floating over the label end on its own backing rather
// than holding a permanent slot
export const TabActions = styled.div`
	position: absolute;
	right: 3px;
	top: 50%;
	transform: translateY(-50%);
	display: flex;
	align-items: center;
	gap: 2px;
	background: ${({ theme }) => theme.gray[100]};
	border-radius: 4px;
	opacity: 0;
	pointer-events: none;
	transition: opacity 0.15s ease;
	z-index: 2;
`;

export const TabActionButton = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 16px;
	height: 16px;
	border-radius: 4px;
	color: ${({ theme }) => theme.gray[1000]};
	user-select: none;

	&:hover {
		background: ${({ theme }) => theme.gray[400]};
	}

	&:focus-visible {
		outline: none;
		box-shadow: 0 0 0 2px ${({ theme }) => theme.blue[500]};
	}
`;

export const Tab = styled.button<{ $active?: boolean }>`
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 0 12px;
	height: 100%;
	border: none;
	background: ${({ $active, theme }) => ($active ? theme.gray[100] : 'transparent')};
	color: ${({ $active, theme }) => ($active ? theme.gray[1000] : theme.gray[700])};
	font-family: 'Inter', sans-serif;
	font-size: 13px;
	font-weight: 500;
	transition: color 0.2s ease-in-out, background-color 0.2s ease-in-out;
	user-select: none;
	outline: none;
	// sits above the bar's hairline, so an active tab's fill breaks the line
	position: relative;
	z-index: 1;
	white-space: nowrap;
	flex: 0 1 auto;
	min-width: 28px;
	max-width: 160px;

	&:hover {
		background: ${({ theme }) => theme.gray[100]};
		color: ${({ theme }) => theme.gray[1000]};
	}

	&:focus-visible {
		outline: none;
		box-shadow: inset 0 0 0 2px ${({ theme }) => theme.blue[500]};
	}

	// the close button is hover-revealed, so keyboard focus has to reveal it too
	&:hover ${TabActions}, &:focus-within ${TabActions} {
		opacity: 1;
		pointer-events: auto;
	}
`;

export const TabLabel = styled.span`
	white-space: nowrap;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	flex: 1;
`;

export const AddButton = styled(BarIconButton)`
	margin-left: 4px;
`;

export const ButtonGroup = styled.div`
	display: flex;
	gap: 0px;
	align-items: center;
	flex-shrink: 0;
`;

// a div because it nests inside the Radix dropdown trigger's own button
export const DropdownTrigger = BarIconTrigger;
